import { Injectable, OnDestroy } from '@angular/core';
import { ActivationStart, Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject, takeUntil } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class NavigationService implements OnDestroy {
	public currentRoute: string = '';
	public routeParts$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
	public matrixParams$: BehaviorSubject<Record<string, string>> = new BehaviorSubject({});

	private destroy$ = new Subject<void>();

	constructor(private router: Router) {
		// Extract the initial matrix params from the URL such as window.location.pathname
		//which looks like this:
		// "/my/journey;sectionUuid=a375339f-df63-49f4-9bfa-d12db6654885;courseUuid=88d01cc0-d27d-46b4-9496-c1af5221c334"
		// this.extractMatrixParams({ url: window.location.pathname } as NavigationEnd);

		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
			//console.log('*** router event:', event);

			if (event instanceof NavigationStart || event instanceof NavigationEnd) {
				this.handleUpdatesFromUrl(event.url);
			}

			if (event instanceof ActivationStart) {
				this.updateParams(event.snapshot.params);
			}
		});
	}

	private handleUpdatesFromUrl(url: string) {
		const [path, ...matrixParts] = url.split(';');
		const routeParts = path.split('/').filter(part => part !== '');
		this.routeParts$.next(routeParts);

		this.currentRoute = routeParts.length > 0 ? routeParts[0] : '';

		const matrixParams: Record<string, string> = this.extractMatrixParamsFromUrl(url);

		this.updateParams(matrixParams);
	}

	public extractMatrixParamsFromUrl(url: string): Record<string, string> {
		const [path, ...matrixParts] = url.split(';');

		const matrixParams: Record<string, string> = {};
		matrixParts.forEach(part => {
			const [key, value] = part.split('=');
			if (key && value) {
				matrixParams[key] = decodeURIComponent(value);
			}
		});

		return matrixParams;
	}

	private updateParams(matrixParams: Record<string, string>): void {
		//only next the params if they have changed
		if (JSON.stringify(matrixParams) !== JSON.stringify(this.matrixParams$.value)) {
			// console.info('matrix params changed:', matrixParams);
			this.matrixParams$.next(matrixParams);
		} else {
			// console.warn('skipping matrix update as they are the same.');
		}
	}

	public navigateToPathName(pathname: string): void {
		//String may be styled like so:/journey;sectionUuid=a375339f-df63-49f4-9bfa-d12db6654885;courseUuid=88d01cc0-d27d-46b4-9496-c1af5221c334;locationUuid=829c053b-dd0f-49f0-a90b-8c5bbeea3f38
		//If it's a matrix url extract the matrix and navigate using navigateWithMatrix$
		if (pathname.includes(';')) {
			const [path, ...matrixParts] = pathname.split(';');
			const matrixParams: Record<string, string> = {};
			matrixParts.forEach(part => {
				const [key, value] = part.split('=');
				if (key && value) {
					matrixParams[key] = decodeURIComponent(value);
				}
			});

			this.navigateWithMatrix$([path], matrixParams);
		} else {
			this.router.navigateByUrl(pathname);
		}
	}

	public navigate(commands: any[]): void {
		this.router.navigate(commands);
	}

	public async navigateWithMatrix$(
		commands: any[],
		matrixParams: Record<string, string>,
		appendParams: boolean = true
	): Promise<void> {
		const baseUrl = this.router.serializeUrl(this.router.createUrlTree(commands));

		const existingParams = appendParams ? await firstValueFrom(this.matrixParams$) : {};

		const matrixParts = Object.entries({ ...existingParams, ...matrixParams })
			.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
			.join(';');

		const fullUrl = matrixParts ? `${baseUrl};${matrixParts}` : baseUrl;

		this.router.navigateByUrl(fullUrl);
	}

	public navigateByFullUrl(url: string): void {
		window.location.href = url;
	}

	public navigateByUrl(url: string, andReload: boolean = false): void {
		this.extractMatrixParamsFromUrl(url);
		this.router.navigateByUrl(url).then(() => {
			if (andReload) {
				window.location.reload();
			}
		});
	}

	public navigateWithReload(commands: any[]): void {
		this.router.navigate(commands).then(() => {
			window.location.reload();
		});
	}

	public async navigateWithMatrixNewWindow$(
		subdomain: string,
		commands: any[],
		matrixParams: Record<string, string>,
		appendParams: boolean = true
	): Promise<void> {
		const baseUrl = this.router.serializeUrl(this.router.createUrlTree(commands));
		const existingParams = appendParams ? await firstValueFrom(this.matrixParams$) : {};

		const matrixParts = Object.entries({ ...existingParams, ...matrixParams })
			.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
			.join(';');

		const fullUrl = matrixParts
			? `https://${subdomain}.mira-learn.com${baseUrl};${matrixParts}`
			: baseUrl;

		// Open the URL in a new window
		window.open(fullUrl, '_blank');
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
